import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiActivity, FiRefreshCw, FiLogOut, FiDownload, FiUpload, FiDatabase, FiClock, FiCopy } from "react-icons/fi";
import apiClient from "../../api/client";
import "./OnlineSessionPanel.css";

const errText = (err, fb) => err?.response?.data?.error || err?.response?.data?.message || err?.message || fb;
const v = (x) => (x === null || x === undefined || x === "" ? "—" : String(x));

/**
 * Live RADIUS session of an Internet / DVPL account (internet_online_session.php):
 * addresses, NAS, MAC, running package, session time and data used, with Log out.
 * Only an online customer has a session; offline shows a short note instead.
 */
const OnlineSessionPanel = ({ accountId, isOnline, onLoggedOut }) => {
  const [state, setState] = useState({ loading: false, error: "", online: null, session: null });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await apiClient.get(`/customer/internet_online_session.php?id=${accountId}`);
      if (my !== seq.current) return;
      if (!data.success) throw new Error(data.error || "Could not load the session");
      setState({ loading: false, error: "", online: !!data.online, session: data.session || null });
    } catch (err) {
      if (my !== seq.current) return;
      setState({ loading: false, error: errText(err, "Could not load the session"), online: null, session: null });
    }
  }, [accountId]);

  useEffect(() => {
    if (isOnline) load();
    else setState({ loading: false, error: "", online: false, session: null });
  }, [accountId, isOnline, load]);

  const logout = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/customer/internet_online_session.php", { id: accountId, action: "logout" });
      if (!data.success) throw new Error(data.error || "Log out failed");
      toast.success(data.message || "Session logged out");
      setConfirm(false);
      setState({ loading: false, error: "", online: false, session: null });
      onLoggedOut && onLoggedOut();
    } catch (err) {
      toast.error(errText(err, "Log out failed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"), () => {});
  };

  const s = state.session;

  return (
    <div className="ols">
      <div className="ols-head">
        <div className="ols-title">
          <FiActivity aria-hidden="true" />
          <h3>Online Session</h3>
          {state.online === true && <span className="ols-pill ols-pill--on"><span className="ols-dot" />Online</span>}
          {state.online === false && <span className="ols-pill ols-pill--off"><span className="ols-dot" />Offline</span>}
        </div>
        <div className="ols-actions">
          <button type="button" className="ols-btn" onClick={load} disabled={state.loading || busy}>
            <FiRefreshCw aria-hidden="true" className={state.loading ? "ols-spin" : ""} />
            {state.online === false && !isOnline ? "Check now" : "Refresh"}
          </button>
          {state.online && (
            <button type="button" className="ols-btn ols-btn--danger" onClick={() => setConfirm(true)} disabled={busy}>
              <FiLogOut aria-hidden="true" /> Log out
            </button>
          )}
        </div>
      </div>

      {confirm && (
        <div className="ols-confirm" role="alertdialog">
          <div>
            <strong>Log out this session?</strong>
            <p>The customer is disconnected now. Their device usually reconnects by itself unless the account is expired or disabled.</p>
          </div>
          <div className="ols-confirm__btns">
            <button type="button" className="ols-btn" onClick={() => setConfirm(false)} disabled={busy}>Cancel</button>
            <button type="button" className="ols-btn ols-btn--danger-solid" onClick={logout} disabled={busy}>
              {busy ? "Logging out…" : "Yes, log out"}
            </button>
          </div>
        </div>
      )}

      {state.loading && !s ? (
        <div className="ols-empty"><span className="ols-spinner" /> Reading the live session…</div>
      ) : state.error ? (
        <div className="ols-empty ols-empty--bad">
          {state.error}
          <button type="button" className="ols-btn" onClick={load}>Try again</button>
        </div>
      ) : state.online === false ? (
        <div className="ols-empty">
          <div className="ols-empty__icon">📴</div>
          <strong>The customer is offline</strong>
          <span>Session details are available only while the customer is online.</span>
        </div>
      ) : s ? (
        <>
          <div className="ols-stats">
            <div className="ols-stat"><FiDatabase aria-hidden="true" /><span>Total data</span><strong>{v(s.total_data)}</strong></div>
            <div className="ols-stat"><FiDownload aria-hidden="true" /><span>Download</span><strong>{v(s.total_download)}</strong></div>
            <div className="ols-stat"><FiUpload aria-hidden="true" /><span>Upload</span><strong>{v(s.total_upload)}</strong></div>
            <div className="ols-stat"><FiClock aria-hidden="true" /><span>Session time</span><strong>{v(s.session_time)}</strong></div>
          </div>

          <div className="ols-grid">
            <section className="ols-card">
              <h4>Connection</h4>
              <dl>
                <div><dt>Started</dt><dd>{v(s.acctstarttime)}</dd></div>
                <div><dt>Login type</dt><dd>{v(s.login_type)}</dd></div>
                <div><dt>Protocol</dt><dd>{v(s.protocol)}</dd></div>
                <div><dt>Running package</dt><dd>{v(s.running_package)}</dd></div>
                <div><dt>Branch</dt><dd>{v(s.branch_name)}</dd></div>
              </dl>
            </section>
            <section className="ols-card">
              <h4>Network</h4>
              <dl>
                <div><dt>IPv4</dt><dd className="ols-mono">{v(s.login_ip_address)}</dd></div>
                <div><dt>IPv6</dt><dd className="ols-mono">{v(s.ip_v6)}</dd></div>
                <div><dt>NAT IP</dt><dd className="ols-mono">{v(s.nat_ip_address)}</dd></div>
                <div><dt>NAS IP</dt><dd className="ols-mono">{v(s.nas_ip)}</dd></div>
                <div><dt>MAC address</dt><dd className="ols-mono">{v(s.login_mac_address)}</dd></div>
              </dl>
            </section>
          </div>

          <div className="ols-foot">
            <span>Session ID</span>
            <code>{v(s.acctuniqueid)}</code>
            {s.acctuniqueid && (
              <button type="button" className="ols-icon" onClick={() => copy(s.acctuniqueid)} title="Copy session ID" aria-label="Copy session ID">
                <FiCopy aria-hidden="true" />
              </button>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default OnlineSessionPanel;
